import { useEffect, useState } from 'react';
import type { Meal, MealEntry } from '../lib/api';
import { filteredIngredients } from '../lib/meals';

// MealBlock renders one meal as either an expanded recipe card (unchecked) or
// a compact strikethrough row (checked). Designed to live inside the
// `<Card eyebrow="Meals">` wrapper on Today — an outlined panel, no fill, so
// each meal reads as a self-contained cooking task without stacking boxes
// inside boxes. Once eaten it collapses to a plain quiet row.
export function MealBlock({
  meal,
  template,
  onToggle,
}: {
  meal: MealEntry;
  template: Meal | undefined;
  onToggle: (next: boolean) => void;
}) {
  const label = template?.label ?? defaultLabel(meal.meal_id);
  const time = template?.scheduled_time ?? '';

  if (meal.completed) {
    return (
      <CompactRow
        label={label}
        completedAt={meal.completed_at}
        time={time}
        onUncheck={() => onToggle(false)}
      />
    );
  }

  const ingredients = filteredIngredients(template);
  const macros = macrosLine(template);
  const prep = template?.prep_notes;

  return (
    <div className="rounded-panel border border-line p-4 space-y-3">
      <header className="flex items-start gap-3">
        <CheckboxButton
          checked={false}
          ariaLabel={`Mark ${label} eaten`}
          onChange={onToggle}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[15px] font-bold tracking-[-0.01em] text-fg">{label}</span>
            {time && (
              <span className="font-mono text-[11px] tabular-nums text-fg-faint shrink-0">
                {time}
              </span>
            )}
          </div>
          {macros && (
            <p className="font-mono text-[11px] tabular-nums text-fg-muted mt-0.5">{macros}</p>
          )}
        </div>
      </header>

      {ingredients.length > 0 && (
        <div className="space-y-1.5">
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
            Ingredients
          </p>
          <ul className="space-y-1 pl-1">
            {ingredients.map((ing, i) => (
              <li key={`${i}-${ing.item}`} className="text-sm text-fg flex gap-2">
                <span className="text-fg-faint shrink-0">·</span>
                <span>
                  <span className="font-mono tabular-nums text-fg-muted">{ing.amount}</span>
                  {' '}
                  {ing.item}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {prep && (
        <div className="space-y-1.5">
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
            Prep
          </p>
          <p className="text-xs text-fg-muted leading-relaxed whitespace-pre-line">
            {prep}
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={() => onToggle(true)}
        className="btn-primary w-full py-2.5 text-sm font-mono uppercase tracking-[0.16em]"
      >
        Mark eaten
      </button>
    </div>
  );
}

// How long a "Copied" / "Copy failed" answer stays on the button before it
// goes back to offering the action again.
const SHARE_FEEDBACK_MS = 2200;

type ShareState = 'idle' | 'busy' | 'copied' | 'error';

// ShareMealsButton hands `text` to the OS share sheet, which is what an
// installed iOS PWA gives us, and falls back to the clipboard everywhere the
// sheet doesn't exist (desktop browsers, jsdom). Lives here rather than in a
// component of its own because both call sites — Today's "Meals" card and
// TomorrowPrep — are meal surfaces sharing the same day-plan text.
export function ShareMealsButton({ text }: { text: () => string }) {
  const [state, setState] = useState<ShareState>('idle');

  // Every terminal answer is temporary, so the button can never be left
  // reading "Copied" over a plan the user has since changed.
  useEffect(() => {
    if (state !== 'copied' && state !== 'error') return;
    const t = setTimeout(() => setState('idle'), SHARE_FEEDBACK_MS);
    return () => clearTimeout(t);
  }, [state]);

  const onClick = async () => {
    if (state === 'busy') return;
    setState('busy');
    const payload = text();

    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ text: payload });
        setState('idle');
        return;
      } catch (e) {
        // Dismissing the sheet is a decision, not a failure — say nothing.
        if (isAbort(e)) {
          setState('idle');
          return;
        }
        // Any other share failure (no user gesture, unsupported payload) is
        // still worth answering, so fall through to the clipboard.
      }
    }

    setState((await copyToClipboard(payload)) ? 'copied' : 'error');
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={state === 'busy'}
      className="btn-secondary shrink-0 inline-flex items-center justify-center min-h-9 px-3.5 py-2 text-[11px] font-mono uppercase tracking-[0.14em] disabled:opacity-50"
    >
      <span aria-live="polite">{shareLabel(state)}</span>
    </button>
  );
}

function shareLabel(state: ShareState): string {
  switch (state) {
    case 'busy':
      return 'Sharing…';
    case 'copied':
      return 'Copied';
    case 'error':
      return 'Copy failed';
    default:
      return 'Share';
  }
}

function isAbort(e: unknown): boolean {
  return typeof e === 'object' && e !== null && (e as { name?: unknown }).name === 'AbortError';
}

// Resolves false instead of throwing, so the caller has exactly one failure
// path whether the clipboard API is missing (jsdom, insecure origin) or the
// write itself was denied.
async function copyToClipboard(text: string): Promise<boolean> {
  const clip = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
  if (!clip || typeof clip.writeText !== 'function') return false;
  try {
    await clip.writeText(text);
    return true;
  } catch {
    return false;
  }
}

// CompactRow is a labelled checkbox: clicking anywhere on the row toggles
// the meal back to uncompleted. The visible "checkbox" is a static span, not
// an interactive control, so the label-wrapped <input> is the single source
// of truth (avoids double-firing onUncheck on click).
function CompactRow({
  label,
  completedAt,
  time,
  onUncheck,
}: {
  label: string;
  completedAt: string | null;
  time: string;
  onUncheck: () => void;
}) {
  const completedTime = formatCompletedTime(completedAt);
  return (
    <label className="flex items-center gap-3 p-2.5 rounded-panel cursor-pointer hover:bg-fg/[0.04] select-none transition-colors">
      <input
        type="checkbox"
        checked
        onChange={() => onUncheck()}
        aria-label={`Mark ${label} not eaten`}
        className="sr-only peer"
      />
      <span
        aria-hidden
        className="grid place-items-center size-5 rounded-[7px] border bg-accent border-accent text-accent-fg shrink-0"
      >
        <CheckIcon />
      </span>
      <div className="flex-1 min-w-0 flex items-baseline justify-between gap-3">
        <span className="text-sm text-fg-muted line-through truncate">
          {label}
          {completedTime && (
            <span className="ml-2 font-mono text-[10px] tracking-[0.14em] text-fg-faint no-underline">
              at {completedTime}
            </span>
          )}
        </span>
        {time && (
          <span className="font-mono text-[11px] tabular-nums text-fg-faint shrink-0">
            {time}
          </span>
        )}
      </div>
    </label>
  );
}

function CheckboxButton({
  checked,
  ariaLabel,
  onChange,
}: {
  checked: boolean;
  ariaLabel: string;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      role="checkbox"
      aria-checked={checked}
      aria-label={ariaLabel}
      className={`mt-0.5 grid place-items-center size-5 rounded-[7px] border transition-all shrink-0 ${
        checked
          ? 'bg-accent border-accent text-accent-fg'
          : 'bg-bg-sunken border-line-strong text-transparent'
      }`}
    >
      <CheckIcon />
    </button>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      className="size-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 8.5l3.5 3.5L13 5" />
    </svg>
  );
}

function macrosLine(template: Meal | undefined): string | null {
  if (!template) return null;
  const parts: string[] = [];
  if (template.calories) parts.push(`${template.calories} kcal`);
  if (template.protein_g != null) parts.push(`${template.protein_g}p`);
  if (template.fat_g != null) parts.push(`${template.fat_g}f`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function defaultLabel(id: MealEntry['meal_id']): string {
  switch (id) {
    case 'breakfast':
      return 'Breakfast';
    case 'lunch':
      return 'Lunch';
    case 'dinner':
      return 'Dinner';
  }
}

// formatCompletedTime returns "HH:MM" for a parseable ISO timestamp, or null
// when the input is missing or unparseable. Returning null suppresses the
// "at —" badge entirely rather than rendering "Invalid Date".
//
// Exported for unit tests.
export function formatCompletedTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
